import baseAPI from "@/api/base";
import { AxiosResponse } from "axios";
import { ICPAttachment } from "./icp-attachment";

export interface ICP {
  id: string;
  name: string;
  organizationId: string;
  companyProfile: string | null;
  personaDefinition: string | null;
  painPoints: string | null;
  solutionDetails: string | null;
  proofStatements: string | null;
  topCompetitors: string | null;
  customerLookalikes: string | null;
  discoveryQuestions: string | null;
  callToAction: string | null;
  toneOfVoice: string | null;
  preferredEmailLength: number;
  additionalInfo: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments?: ICPAttachment[];
}

export interface CreateICPRequest {
  name: string;
  companyProfile?: string;
  personaDefinition?: string;
  painPoints?: string;
  solutionDetails?: string;
  proofStatements?: string;
  topCompetitors?: string;
  customerLookalikes?: string;
  discoveryQuestions?: string;
  callToAction?: string;
  toneOfVoice?: string;
  preferredEmailLength?: number;
  additionalInfo?: string;
  isDefault?: boolean;
}

export interface UpdateICPRequest {
  name?: string;
  companyProfile?: string;
  personaDefinition?: string;
  painPoints?: string;
  solutionDetails?: string;
  proofStatements?: string;
  topCompetitors?: string;
  customerLookalikes?: string;
  discoveryQuestions?: string;
  callToAction?: string;
  toneOfVoice?: string;
  preferredEmailLength?: number;
  additionalInfo?: string;
  isDefault?: boolean;
}

class ICPService {
  private static BASE_URL = "/icp";

  /**
   * Get all ICPs for the organization
   */
  static getICPs = async (): Promise<AxiosResponse<{ status: boolean; data: ICP[] }>> => {
    const response: AxiosResponse = await baseAPI.get(this.BASE_URL);
    return response;
  };

  /**
   * Get a single ICP by ID
   */
  static getICP = async (id: string): Promise<AxiosResponse<{ status: boolean; data: ICP }>> => {
    const response: AxiosResponse = await baseAPI.get(`${this.BASE_URL}/${id}`);
    return response;
  };

  /**
   * Create a new ICP
   */
  static createICP = async (
    data: CreateICPRequest
  ): Promise<AxiosResponse<{ status: boolean; data: ICP; msg: string }>> => {
    const response: AxiosResponse = await baseAPI.post(this.BASE_URL, data);
    return response;
  };

  /**
   * Update an existing ICP
   */
  static updateICP = async (
    id: string,
    data: UpdateICPRequest
  ): Promise<AxiosResponse<{ status: boolean; data: ICP; msg: string }>> => {
    const response: AxiosResponse = await baseAPI.put(`${this.BASE_URL}/${id}`, data);
    return response;
  };

  /**
   * Delete an ICP (soft delete)
   */
  static deleteICP = async (
    id: string
  ): Promise<AxiosResponse<{ status: boolean; data: { id: string }; msg: string }>> => {
    const response: AxiosResponse = await baseAPI.delete(`${this.BASE_URL}/${id}`);
    return response;
  };

  /**
   * Set an ICP as default
   */
  static setDefaultICP = async (
    id: string
  ): Promise<AxiosResponse<{ status: boolean; data: ICP; msg: string }>> => {
    const response: AxiosResponse = await baseAPI.post(`${this.BASE_URL}/${id}/default`);
    return response;
  };
}

export default ICPService;
